import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';

export interface LightboxItem { src: string; caption?: ReactNode; alt?: string }

/**
 * Imagem em tamanho grande por cima da página: setas e teclado para navegar, Esc ou clique fora para fechar.
 * `actions` entra ao lado da legenda (ex.: "ir ao trecho").
 */
export function Lightbox({ items, index, onClose, onIndex, actions }: { items: LightboxItem[]; index: number | null; onClose: () => void; onIndex?: (i: number) => void; actions?: (item: LightboxItem, i: number) => ReactNode }) {
  const [i, setI] = useState(index ?? 0);
  useEffect(() => { if (index !== null) setI(index); }, [index]);
  const open = index !== null && items.length > 0;
  const go = (n: number) => { const next = (n + items.length) % items.length; setI(next); onIndex?.(next); };
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); else if (e.key === 'ArrowRight') go(i + 1); else if (e.key === 'ArrowLeft') go(i - 1); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, i]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) return null;
  const item = items[Math.min(i, items.length - 1)]!;
  return createPortal(
    <div className="rr-lightbox" role="dialog" aria-modal="true" onClick={onClose}>
      <button type="button" className="rr-lightbox__close" onClick={onClose} aria-label="fechar"><Icon name="x" size={20} /></button>
      {items.length > 1 && <button type="button" className="rr-lightbox__nav rr-lightbox__nav--prev" onClick={(e) => { e.stopPropagation(); go(i - 1); }} aria-label="anterior"><Icon name="chevron-down" size={22} style={{ transform: 'rotate(90deg)' }} /></button>}
      <figure className="rr-lightbox__figure" onClick={(e) => e.stopPropagation()}>
        <img className="rr-lightbox__img" src={item.src} alt={item.alt ?? ''} />
        {(item.caption || actions) && (
          <figcaption className="rr-lightbox__caption">
            <span>{item.caption}</span>
            {actions && <span className="rr-lightbox__actions">{actions(item, i)}</span>}
            {items.length > 1 && <span className="rr-lightbox__count">{i + 1} / {items.length}</span>}
          </figcaption>
        )}
      </figure>
      {items.length > 1 && <button type="button" className="rr-lightbox__nav rr-lightbox__nav--next" onClick={(e) => { e.stopPropagation(); go(i + 1); }} aria-label="próxima"><Icon name="chevron-down" size={22} style={{ transform: 'rotate(-90deg)' }} /></button>}
    </div>,
    document.body,
  );
}
